import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IconButton, styled } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DownloadIcon from '@mui/icons-material/Download';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import Editor from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { useSnackbar } from 'notistack';
import { localStorageRepositorySingleton } from '@warehouse/shared/util';
import { EditViewModeToggleButton, ErrorsMessagesComponent } from '@warehouse/shared/ui';

export const THEME_STORAGE_KEY = 'monaco-editor-theme-preference';

const EditorWrapper = styled('div')({
	display: 'flex',
	flex: 1,
	minHeight: 0,
	flexDirection: 'column',
	gap: '8px',
	padding: '4px 25px 10px 25px',
	
});

const EditorToolbar = styled('div')({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	padding: '-10px 0 0 20px',
});

const ToolbarLeft = styled('div')({
	display: 'flex',
	alignItems: 'center',
	gap: '20px',
});

const ToolbarRight = styled('div')({
	display: 'flex',
	alignItems: 'center',
	gap: '4px',
});

const EditorContainer = styled('div')(({ theme }) => ({
	flex: 1,
	minHeight: 0,
	position: 'relative',
	borderRadius: theme.spacing(1),
	overflow: 'hidden',
	border: `1px solid ${theme.palette.divider}`,
}));

export type MonacoEditorHandle = {
	getValue: () => string;
};

type MonacoEditorProps = {
	content?: string;
	language?: string;
	errors?: string[];
	editModeAvailability?: boolean;
	showDownloadButton?: boolean;
	onChange?: (value: string) => void;
	onDirtyChange?: (isDirty: boolean) => void;
	title?: string;
};

export const MonacoEditor = forwardRef<MonacoEditorHandle, MonacoEditorProps>((
	{
		content,
		language = 'xml',
		errors = [],
		editModeAvailability = true,
		showDownloadButton = false,
		onChange,
		onDirtyChange,
		title = 'data',
	},
	ref,
) => {
	const [isDark, setIsDark] = useState<boolean>(() => {
		const storedTheme = localStorageRepositorySingleton.get().get(THEME_STORAGE_KEY);
		return storedTheme !== 'light';
	});
	const [editMode, setEditMode] = useState(false);
	const [canUndo, setCanUndo] = useState(false);
	const [canRedo, setCanRedo] = useState(false);
	const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
	const initialVersionRef = useRef<number>(0);
	const lastVersionRef = useRef<number>(0);

	const { enqueueSnackbar } = useSnackbar();

	useImperativeHandle(
		ref,
		() => ({
			getValue: () => editorRef.current?.getValue() ?? content ?? '',
		}),
		[content],
	);

	useEffect(() => {
		localStorageRepositorySingleton.get().set(THEME_STORAGE_KEY, isDark ? 'dark' : 'light');
	}, [isDark]);

	useEffect(() => {
		onDirtyChange?.(canUndo);
	}, [canUndo, onDirtyChange]);

	const handleCopy = async () => {
		try {
			const editorContent = editorRef.current?.getValue() ?? content ?? '';
			await navigator.clipboard.writeText(editorContent);
			enqueueSnackbar('Copied to clipboard', { variant: 'success', preventDuplicate: true });
		} catch (error: any) {
			enqueueSnackbar(error.message || 'Failed to copy to clipboard', { variant: 'error', preventDuplicate: true });
		}
	};

	const handleToggleTheme = () => setIsDark((v) => !v);

	const handleDownload = () => {
		try {
			const editorContent = editorRef.current?.getValue() ?? content ?? '';
			const blob = new Blob([editorContent], { type: language === 'json' ? 'application/json' : 'text/xml' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `${title || 'data'}.${language === 'json' ? 'json' : 'xml'}`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
			enqueueSnackbar('File downloaded successfully', { variant: 'success', preventDuplicate: true });
		} catch (error: any) {
			enqueueSnackbar(error.message || 'Failed to download file', { variant: 'error', preventDuplicate: true });
		}
	};

	const handleEditorDidMount = useCallback(
		(editorInstance: editor.IStandaloneCodeEditor) => {
			editorRef.current = editorInstance;

			const model = editorInstance.getModel();
			if (model) {
				const version = model.getAlternativeVersionId();
				initialVersionRef.current = version;
				lastVersionRef.current = version;
				setCanUndo(false);
				setCanRedo(false);
			}

			editorInstance.onDidChangeModelContent(() => {
				const editorModel = editorInstance.getModel();
				if (!editorModel) return;

				const currentVersion = editorModel.getAlternativeVersionId();
				setCanUndo(currentVersion !== initialVersionRef.current);
				setCanRedo(currentVersion < lastVersionRef.current);

				if (currentVersion > lastVersionRef.current) {
					lastVersionRef.current = currentVersion;
				}

				onChange?.(editorModel.getValue());
			});
		},
		[onChange],
	);

	const handleUndo = useCallback(() => {
		editorRef.current?.trigger('keyboard', 'undo', null);
	}, []);

	const handleRedo = useCallback(() => {
		editorRef.current?.trigger('keyboard', 'redo', null);
	}, []);

	const defineCustomTheme = useCallback(
		(monaco: Parameters<NonNullable<React.ComponentProps<typeof Editor>['beforeMount']>>[0]) => {
			monaco.editor.defineTheme('nxMonacoDarkTheme', {
				base: 'vs-dark',
				inherit: true,
				rules: [
					{
						foreground: 'ee6a6f',
						token: 'tag.xml',
					},
					{
						foreground: 'a3ce9e',
						token: 'attribute.value.xml',
					},
					{
						foreground: 'c594c5',
						token: 'attribute.name.xml',
					},
				],
				colors: {},
			});
		},
		[],
	);

	return (
		<EditorWrapper>
			<EditorToolbar>
				<ToolbarLeft>
					{editModeAvailability && (
						<>
							<EditViewModeToggleButton
								onClickHandler={(editModeValue: boolean) => setEditMode(editModeValue)}
								editMode={editMode}
							/>

							<IconButton size="small" onClick={handleUndo} disabled={!canUndo} aria-label="Undo" title="Undo">
								<UndoIcon fontSize="small" />
							</IconButton>

							<IconButton size="small" onClick={handleRedo} disabled={!canRedo} aria-label="Redo" title="Redo">
								<RedoIcon fontSize="small" />
							</IconButton>
						</>
					)}
				</ToolbarLeft>
			
				<ToolbarRight>
					<IconButton size="small" onClick={handleToggleTheme} aria-label="Toggle Theme" title="Toggle Theme">
						{isDark ? <LightModeRoundedIcon fontSize="small" /> : <DarkModeRoundedIcon fontSize="small" />}
					</IconButton>

					<IconButton
						size="small"
						onClick={handleCopy}
						aria-label={`Copy ${language.toUpperCase()}`}
						title={`Copy ${language.toUpperCase()}`}
					>
						<ContentCopyRoundedIcon fontSize="small" />
					</IconButton>

					{showDownloadButton && (
						<IconButton
							size="small"
							onClick={handleDownload}
							aria-label={`Download ${language.toUpperCase()}`}
							title={`Download ${language.toUpperCase()}`}
						>
							<DownloadIcon fontSize="small" />
						</IconButton>
					)}
				</ToolbarRight>
			</EditorToolbar>
			{errors.length > 0 && <ErrorsMessagesComponent errorsList={errors} />}
			<EditorContainer data-testid="xml-editor" data-language={language}>
				<Editor
					height="100%"
					defaultLanguage={language}
					theme={isDark ? 'nxMonacoDarkTheme' : 'vs'}
					value={content}
					beforeMount={defineCustomTheme}
					onMount={handleEditorDidMount}
					saveViewState
					options={{
						minimap: { enabled: false },
						wordWrap: 'on',
						folding: true,
						automaticLayout: true,
						readOnly: !editMode,
						readOnlyMessage: { value: 'Read-only mode selected' },
					}}
				/>
			</EditorContainer>
		</EditorWrapper>
	);
});
